import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit, Trash2, Plus, CheckCircle2, Loader2, FileText, Calendar, Tag, Store, DollarSign, Shield, Info, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useFamily } from '@/context/FamilyContext';
import { useItem } from '@/hooks/useItems';
import { useReminders } from '@/hooks/useReminders';
import { useFiles } from '@/hooks/useFiles';
import { useServiceLogs } from '@/hooks/useServiceLogs';
import { formatDate, urgencyClass, urgencyDot, urgencyLabel, daysUntil } from '@/lib/dateUtils';
import { TopBar } from '@/components/TopBar';
import { FilePreview } from '@/components/FilePreview';
import { FileUpload } from '@/components/FileUpload';
import { ServiceDoneModal } from '@/components/ServiceDoneModal';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import type { Reminder } from '@/types';

export function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canEdit, isOwner } = useFamily();
  const { item, loading: itemLoading } = useItem(id!);
  const { reminders, reload: reloadReminders } = useReminders(id!);
  const { files, reload: reloadFiles } = useFiles(id!);
  const { logs, reload: reloadLogs } = useServiceLogs(id!);
  const [activeDone, setActiveDone] = useState<Reminder | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (itemLoading) return <LoadingSpinner className="h-64" />;
  if (!item) return <div className="p-8 text-center text-gray-500">Item not found.</div>;

  const deleteItem = async () => {
    if (!confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    // Delete all files from storage
    if (files.length > 0) {
      await supabase.storage.from('invoices').remove(files.map((f) => f.storage_path));
    }
    const { error } = await supabase.from('items').delete().eq('id', id!);
    setDeleting(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Item deleted');
    navigate('/items');
  };

  const InfoRow = ({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | null | undefined }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
        <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-xs text-gray-400">{label}</p>
          <p className="text-sm text-gray-800 font-medium">{value}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="pb-8">
      <TopBar
        title={item.name}
        back="/items"
        right={
          canEdit && (
            <div className="flex gap-1">
              <button onClick={() => navigate(`/items/${id}/edit`)} className="p-2 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors">
                <Edit className="w-4 h-4" />
              </button>
              {(canEdit || isOwner) && (
                <button onClick={deleteItem} disabled={deleting} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              )}
            </div>
          )
        }
      />

      <div className="px-4 py-4 space-y-5">
        {/* Type badge */}
        <div className="flex items-center gap-2">
          <span className={`text-xs px-3 py-1 rounded-full font-medium ${
            item.type === 'vehicle' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
          }`}>
            {item.type === 'vehicle' ? '🚗 Vehicle' : '🏠 Appliance'}
          </span>
        </div>

        {/* Details */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-2">
          <InfoRow icon={Tag} label="Brand / Model" value={[item.brand, item.model].filter(Boolean).join(' ')} />
          <InfoRow icon={Info} label={item.type === 'vehicle' ? 'Registration No.' : 'Serial No.'} value={item.serial_or_reg_no} />
          <InfoRow icon={Calendar} label="Purchase date" value={formatDate(item.purchase_date)} />
          <InfoRow icon={Store} label="Store / Dealer" value={item.store} />
          <InfoRow icon={DollarSign} label="Price" value={item.price != null ? `₹${Number(item.price).toLocaleString('en-IN')}` : null} />
          <InfoRow icon={Shield} label="Warranty end" value={formatDate(item.warranty_end)} />
          <InfoRow icon={Info} label="Notes" value={item.notes} />
        </div>

        {/* Reminders */}
        <Section title="Reminders" icon={<Clock className="w-4 h-4" />}>
          {reminders.length === 0
            ? <EmptyState icon={<Clock className="w-5 h-5" />} title="No reminders" description="Edit this item to add reminders." />
            : reminders.map((r) => (
                <ReminderCard key={r.id} reminder={r} canEdit={canEdit} userId={user?.id ?? ''} onDone={() => { setActiveDone(r); }} onUpdated={reloadReminders} />
              ))
          }
        </Section>

        {/* Files */}
        <Section title="Documents & Invoices" icon={<FileText className="w-4 h-4" />}>
          {canEdit && (
            <FileUpload
              familyId={item.family_id}
              itemId={item.id}
              createdBy={user?.id ?? ''}
              onUploaded={reloadFiles}
            />
          )}
          {files.length === 0
            ? <p className="text-sm text-gray-400 text-center py-4">No files yet.</p>
            : files.map((f) => <FilePreview key={f.id} file={f} canDelete={canEdit} onDeleted={reloadFiles} />)
          }
        </Section>

        {/* Service history */}
        <Section title="Service History" icon={<CheckCircle2 className="w-4 h-4" />}>
          {logs.length === 0
            ? <p className="text-sm text-gray-400 text-center py-4">No service logged yet.</p>
            : logs.map((log) => (
                <div key={log.id} className="flex gap-3 py-3 border-b border-gray-50 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">{formatDate(log.done_date)}</p>
                    {log.notes && <p className="text-xs text-gray-500 mt-0.5">{log.notes}</p>}
                  </div>
                </div>
              ))
          }
        </Section>
      </div>

      {activeDone && user && (
        <ServiceDoneModal
          reminder={activeDone}
          userId={user.id}
          onClose={() => setActiveDone(null)}
          onDone={() => { setActiveDone(null); reloadReminders(); reloadLogs(); reloadFiles(); }}
        />
      )}
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-gray-700 mb-3">
        {icon}
        <h2 className="font-semibold text-sm">{title}</h2>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ReminderCard({ reminder, canEdit, onDone, onUpdated }: { reminder: Reminder; canEdit: boolean; userId: string; onDone: () => void; onUpdated: () => void }) {
  const days = daysUntil(reminder.next_due_date);
  const cls = urgencyClass(days);
  const dot = urgencyDot(days);
  const lbl = urgencyLabel(days);

  const toggleActive = async () => {
    await supabase.from('reminders').update({ is_active: !reminder.is_active }).eq('id', reminder.id);
    toast.success(reminder.is_active ? 'Reminder paused' : 'Reminder resumed');
    onUpdated();
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${reminder.is_active ? cls : 'bg-gray-50 border-gray-100 text-gray-400'}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 ${reminder.is_active ? dot : 'bg-gray-300'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{reminder.title}</p>
        <p className="text-xs text-gray-500">{formatDate(reminder.next_due_date)} · {lbl}</p>
        {!reminder.one_time && (
          <p className="text-xs text-gray-400">Every {reminder.repeat_every} {reminder.repeat_unit}</p>
        )}
      </div>
      {canEdit && (
        <div className="flex gap-1 shrink-0">
          {reminder.is_active && (
            <button onClick={onDone} className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 hover:text-emerald-600 transition-colors" title="Mark done">
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={toggleActive} className="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 transition-colors text-xs" title={reminder.is_active ? 'Pause' : 'Resume'}>
            {reminder.is_active ? '⏸' : '▶'}
          </button>
        </div>
      )}
    </div>
  );
}
